import { AdjustAction, adjustHistoryReducer, createAdjustHistory, getAdjustEditCount, getAdjustEditionActions, getAdjustSaveAnalyticsFlags, hasAdjustEdits } from './adjustDocument'
import { AdjustLookId, AdjustToolId, BLUR_BRUSH_SIZE_RANGE, BLUR_STRENGTH_DEFAULT, BlurMode, DEFAULT_DRAW_COLOR, DEFAULT_STICKER_COLOR, DRAW_SIZE_RANGE, DrawTool, StickerFont, isAdjustToolId } from './adjust.types'
import { Point, Size } from './adjustGeometry'
import { createLookThumbnails, createPreviewSource, loadAdjustImage } from './adjustImageLoading'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { AdjustRenderer, exportAdjustedImage } from './adjustRender'
import { AdjustViewMode } from './adjustView'
import { EditionAction } from '../photoStudioTypes'
import { StickerAssets } from './adjustStickers'
import { WebGLAdjustProcessor } from './webglAdjustProcessor'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'

/** What the Photo Studio's save paths need from the Adjust editor (replaces Pintura's `editorRef`) */
export interface AdjustEditorHandle {
  /** The edited photo at full size, cropped, rotated and resized */
  exportFile: () => Promise<File>
  hasEdits: () => boolean
  getEditionActions: () => EditionAction[]
  getSaveAnalyticsFlags: () => Record<string, boolean>
  getEditCount: () => number
}

/** The photo being adjusted: full-size image (for the save) and a smaller copy for the live preview */
export interface AdjustBase {
  url: string
  image: HTMLImageElement
  preview: HTMLCanvasElement
  size: Size
}

export type StickerTab = 'icons' | 'text' | 'logo'

const ZOOM_MIN = 1
const ZOOM_MAX = 8

/**
 * State of the in-house Adjust editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT), shared by its panel — in the
 * One tool column or the Combine tools list — and its canvas. Active only while Adjust is the tool on screen; leaving
 * Adjust (after the auto-save has read the handle) or a new base photo starts a fresh document.
 */
export const [AdjustEditorProvider, useAdjustEditor] = constate(() => {
  const {
    isPinturaReplacement,
    isAdjustTool,
    isBlurTool,
    previewImage,
    sourceVisualFilename,
    adjustEditorRef,
    setAdjustHasEdits,
    selectedPinturaFeature,
  } = useClientPhotoStudio()
  const isActive = Boolean(isPinturaReplacement && isAdjustTool)

  const [history, dispatch] = useReducer(adjustHistoryReducer, undefined, createAdjustHistory)
  const doc = history.present
  const docRef = useRef(doc)
  docRef.current = doc

  const [base, setBase] = useState<AdjustBase | null>(null)
  const baseRef = useRef(base)
  baseRef.current = base
  const [isBaseError, setIsBaseError] = useState(false)
  const [lookThumbnails, setLookThumbnails] = useState<Partial<Record<AdjustLookId, string>>>({})

  const [assetsVersion, setAssetsVersion] = useState(0)
  const assets = useMemo(() => new StickerAssets(() => setAssetsVersion(version => version + 1)), [])
  const processorRef = useRef<WebGLAdjustProcessor | null | undefined>(undefined)
  const rendererRef = useRef<AdjustRenderer | null>(null)
  const getProcessor = useCallback(() => {
    if (processorRef.current === undefined) processorRef.current = WebGLAdjustProcessor.create()
    return processorRef.current
  }, [])
  const getRenderer = useCallback(() => {
    if (!rendererRef.current) rendererRef.current = new AdjustRenderer(getProcessor())
    return rendererRef.current
  }, [getProcessor])
  useEffect(() => () => {
    rendererRef.current?.dispose()
    processorRef.current?.dispose()
  }, [])

  // UI settings of the tools (not part of the document)
  // Blur opens on Brush; Area (privacy rectangles) is one click away
  const [blurMode, setBlurMode] = useState<BlurMode>('brush')
  // Each blur mode keeps its own strength (Brush starts at 3, Area at 6)
  const [blurStrengths, setBlurStrengths] = useState<Record<BlurMode, number>>(BLUR_STRENGTH_DEFAULT)
  const blurStrength = blurStrengths[blurMode]
  const setBlurStrength = useCallback((strength: number) => setBlurStrengths(previous => ({ ...previous, [blurMode]: strength })), [blurMode])
  const [blurBrushSize, setBlurBrushSize] = useState(BLUR_BRUSH_SIZE_RANGE.default)
  const [drawTool, setDrawTool] = useState<DrawTool>('pen')
  const [drawColor, setDrawColor] = useState(DEFAULT_DRAW_COLOR)
  const [drawSize, setDrawSize] = useState(DRAW_SIZE_RANGE.default)
  const [drawOpacity, setDrawOpacity] = useState(100)
  const [stickerTab, setStickerTab] = useState<StickerTab>('icons')
  const [stickerColor, setStickerColor] = useState(DEFAULT_STICKER_COLOR)
  const [stickerFont, setStickerFont] = useState<StickerFont>('sans')
  const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null)

  // View
  const [zoom, setZoomState] = useState(1)
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 })
  const [viewMode, setViewMode] = useState<AdjustViewMode>('after')
  const [split, setSplit] = useState(0.5)
  const setZoom = useCallback((next: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
    setZoomState(clamped)
    if (clamped === ZOOM_MIN) setPan({ x: 0, y: 0 })
  }, [])
  const resetView = useCallback(() => {
    setZoomState(1)
    setPan({ x: 0, y: 0 })
    setViewMode('after')
    setSplit(0.5)
  }, [])

  /**
   * The open tool's section can be collapsed too, leaving every section closed: the canvas then shows the result with no
   * tool on it (`tool` null). `selectedPinturaFeature` keeps the last tool — the save paths need it while in Adjust.
   * Entering Adjust opens its tool again, and so does Blur (its own tool, nothing to collapse) — also from Adjust.
   */
  const [isToolOpen, setToolOpen] = useState(true)
  useEffect(() => {
    if (isActive) setToolOpen(true)
  }, [isActive, isBlurTool])
  const tool: AdjustToolId | null = !isToolOpen ? null : isAdjustToolId(selectedPinturaFeature) ? selectedPinturaFeature : 'finetune'
  useEffect(() => {
    if (tool !== 'sticker') setSelectedStickerId(null)
  }, [tool])

  // A new base photo (entering Adjust, or a Photo Studio undo / redo while there are no edits) starts a new document
  useEffect(() => {
    if (!isActive || !previewImage) return
    let isCancelled = false
    setIsBaseError(false)
    loadAdjustImage(previewImage)
      .then((image) => {
        if (isCancelled) return
        const preview = createPreviewSource(image)
        dispatch({ type: 'reset' })
        setSelectedStickerId(null)
        setBase({ url: previewImage, image, preview, size: { width: image.naturalWidth, height: image.naturalHeight } })
        // Thumbnails off the critical path
        window.setTimeout(() => {
          if (isCancelled) return
          try {
            setLookThumbnails(createLookThumbnails(preview))
          } catch (error) {
            logger.warn('Adjust editor: look thumbnails failed', error, { context: { component: 'PhotoStudio', action: 'adjustLookThumbnails' } })
          }
        }, 0)
      })
      .catch((error) => {
        if (isCancelled) return
        setIsBaseError(true)
        logger.error('Adjust editor: photo failed to load', error, { context: { component: 'PhotoStudio', action: 'adjustLoadBase' } })
      })
    return () => {
      isCancelled = true
    }
  }, [isActive, previewImage])

  useEffect(() => {
    if (isActive) return
    dispatch({ type: 'reset' })
    setBase(null)
    setSelectedStickerId(null)
    resetView()
  }, [isActive, resetView])

  const apply = useCallback((action: AdjustAction, historyKey?: string) => {
    dispatch({ type: 'apply', action, historyKey, now: Date.now() })
  }, [])
  const commit = useCallback(() => dispatch({ type: 'commit' }), [])
  const undo = useCallback(() => {
    setSelectedStickerId(null)
    dispatch({ type: 'undo' })
  }, [])
  const redo = useCallback(() => {
    setSelectedStickerId(null)
    dispatch({ type: 'redo' })
  }, [])

  const exportFile = useCallback(async (): Promise<File> => {
    const current = baseRef.current
    if (!current) throw new Error('adjust_base_not_loaded')
    // PNG stays PNG; everything else saves as JPEG, like Pintura's writer
    const mimeType = /\.png$/i.test(sourceVisualFilename ?? '') ? 'image/png' : 'image/jpeg'
    const blob = await exportAdjustedImage({ doc: docRef.current, image: current.image, processor: getProcessor(), assets, mimeType })
    return new File([blob], mimeType === 'image/png' ? 'adjusted.png' : 'adjusted.jpg', { type: mimeType, lastModified: Date.now() })
  }, [assets, sourceVisualFilename, getProcessor])

  const handle = useMemo<AdjustEditorHandle>(() => ({
    exportFile,
    hasEdits: () => hasAdjustEdits(docRef.current),
    getEditionActions: () => getAdjustEditionActions(docRef.current),
    getSaveAnalyticsFlags: () => getAdjustSaveAnalyticsFlags(docRef.current),
    getEditCount: () => getAdjustEditCount(docRef.current),
  }), [exportFile])

  useEffect(() => {
    if (!isActive) return
    adjustEditorRef.current = handle
    return () => {
      if (adjustEditorRef.current === handle) adjustEditorRef.current = null
    }
  }, [isActive, handle, adjustEditorRef])

  const hasEdits = useMemo(() => hasAdjustEdits(doc), [doc])
  useEffect(() => {
    setAdjustHasEdits(isActive && hasEdits)
  }, [isActive, hasEdits, setAdjustHasEdits])

  return {
    isActive,
    isBaseError,
    base,
    doc,
    hasEdits,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    apply,
    commit,
    undo,
    redo,
    tool,
    setToolOpen,
    assets,
    assetsVersion,
    getRenderer,
    lookThumbnails,
    blurMode,
    setBlurMode,
    blurStrength,
    setBlurStrength,
    blurBrushSize,
    setBlurBrushSize,
    drawTool,
    setDrawTool,
    drawColor,
    setDrawColor,
    drawSize,
    setDrawSize,
    drawOpacity,
    setDrawOpacity,
    stickerTab,
    setStickerTab,
    stickerColor,
    setStickerColor,
    stickerFont,
    setStickerFont,
    selectedStickerId,
    setSelectedStickerId,
    zoom,
    setZoom,
    pan,
    setPan,
    viewMode,
    setViewMode,
    split,
    setSplit,
  }
})
